import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    getUserCategoriesByType,
    getUserCategoriesByUser,
    createUserCategories,
    updateUserCategories,
    saveUserCategories,
    UserCategoryResponse
} from "../Actions/UserCategoryActions";

export enum UserCategoryTypeEnum {
    "Profile" = 1,
    "Emergency" = 2,
}

interface UserCategoryState {
    userCategories: { [key: string]: UserCategoryResponse | null }; // key: `${userId}-${type}`
    loading: boolean;
    error: string | null;
}

const initialState: UserCategoryState = {
    userCategories: {},
    loading: false,
    error: null,
};

const userCategorySlice = createSlice({
    name: "userCategory",
    initialState,
    reducers: {
        clearUserCategories: (state) => {
            state.userCategories = {};
            state.error = null;
        },
        clearUserCategoryByKey: (state, action: PayloadAction<string>) => {
            delete state.userCategories[action.payload];
        },
        clearUserCategoryByUserId: (state, action: PayloadAction<string>) => {
            // Clear all categories for a specific user
            Object.keys(state.userCategories).forEach(key => {
                if (key.startsWith(`${action.payload}-`)) {
                    delete state.userCategories[key];
                }
            });
        },
    },
    extraReducers: (builder) => {
        builder
            // Get user categories by type
            .addCase(getUserCategoriesByType.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUserCategoriesByType.fulfilled, (state, action) => {
                state.loading = false;
                const { userId, type } = action.meta.arg;
                const key = `${userId}-${type}`;
                state.userCategories[key] = action.payload;
                state.error = null;
            })
            .addCase(getUserCategoriesByType.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload === "string" ? action.payload : "Failed to fetch user categories";
            })

            // Get user categories by user (no type)
            .addCase(getUserCategoriesByUser.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUserCategoriesByUser.fulfilled, (state, action) => {
                state.loading = false;
                const { userId } = action.meta.arg;
                const key = `${userId}-all`;
                // Store the raw payload under the -all key
                (state.userCategories as any)[key] = action.payload as any;
                state.error = null;
            })
            .addCase(getUserCategoriesByUser.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload === "string" ? action.payload : "Failed to fetch user categories by user";
            })
            
            // Create user categories
            .addCase(createUserCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createUserCategories.fulfilled, (state, action) => {
                state.loading = false;
                const { userId, type } = action.meta.arg;
                const key = `${userId}-${type}`;
                state.userCategories[key] = action.payload;
                state.error = null;
            })
            .addCase(createUserCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload === "string" ? action.payload : "Failed to create user categories";
            })
            
            // Update user categories
            .addCase(updateUserCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateUserCategories.fulfilled, (state, action) => {
                state.loading = false;
                const { userId, type } = action.meta.arg.data;
                const key = `${userId.id}-${type.id}`;
                state.userCategories[key] = action.payload;
                state.error = null;
            })
            .addCase(updateUserCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload === "string" ? action.payload : "Failed to update user categories";
            })
            
            // Save user categories (smart create/update)
            .addCase(saveUserCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(saveUserCategories.fulfilled, (state, action) => {
                state.loading = false;
                const { userId, type } = action.meta.arg;
                const key = `${userId}-${type}`;
                state.userCategories[key] = action.payload;
                state.error = null;
            })
            .addCase(saveUserCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = typeof action.payload === "string" ? action.payload : "Failed to save user categories";
            });
    },
});

export const { clearUserCategories, clearUserCategoryByKey, clearUserCategoryByUserId } = userCategorySlice.actions;
export default userCategorySlice.reducer;
